import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

export const DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER = Object.freeze([
  // BonBoZ: panel CRM len DAU. Truc page nhin ho so khach lien tuc trong suot
  // cuoc chat, con assignee/nhan chi dat mot lan moi hoi thoai. Day chi la thu
  // tu MAC DINH - moi nguoi keo tha sap xep lai duoc, luu rieng theo tai khoan.
  // (Logic merge ben duoi tu them muc nay cho user da luu thu tu sidebar cu.)
  { name: 'epoint_crm' },
  { name: 'conversation_actions' },
  { name: 'macros' },
  { name: 'conversation_info' },
  { name: 'contact_attributes' },
  { name: 'contact_notes' },
  { name: 'shared_files' },
  { name: 'previous_conversation' },
  { name: 'conversation_participants' },
  { name: 'linear_issues' },
  { name: 'shopify_orders' },
]);

// BonBoZ: cac muc AN HAN khoi sidebar hoi thoai.
// Sidebar dai 11 muc, moi muc mot thanh tieu de - muc khong dung chi lam kho doc.
// Bo ten vao/ra day la du, KHONG can sua ContactPanel.vue: chuoi v-if o do khong
// co nhanh v-else cuoi, ten nao khong khop thi khong render gi ca.
// Van giu day du muc trong DEFAULT_... o tren de bo ten khoi day la hien lai duoc.
export const HIDDEN_CONVERSATION_SIDEBAR_ITEMS = Object.freeze([
  // Chua dung toi.
  'macros',
  // Trinh duyet / he dieu hanh / IP / trang dan toi - CHI web widget moi co.
  // Kenh Facebook khong bao gio dien duoc, muc nay luon rong.
  'conversation_info',
  // Trung voi panel "Ho so khach": 6/8 thuoc tinh o day panel da hien, hien dep
  // hon (the dich vu tach 3 nhom thay vi mot con so). Hai cai panel chua hien
  // (tuoi, gioi tinh) da chuyen thanh sua tai cho trong panel.
  // Dinh nghia thuoc tinh VAN GIU (lib/tasks/epoint.rake) de con loc/phan nhom
  // khach o man hinh Contacts - an o day chi la an cho hien thi.
  'contact_attributes',
  // Doi truc page nho, khong dung co che nhieu nguoi cung theo mot hoi thoai.
  'conversation_participants',
  // Tich hop chua bat.
  'linear_issues',
  'shopify_orders',
]);

export const DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER = Object.freeze([
  { name: 'contact_attributes' },
  { name: 'contact_labels' },
  { name: 'previous_conversation' },
]);

/**
 * Slugifies the channel name.
 * Replaces spaces, hyphens, and double colons with underscores.
 * @param {string} name - The channel name to slugify.
 * @returns {string} The slugified channel name.
 */
const slugifyChannel = name =>
  name?.toLowerCase().replace(' ', '_').replace('-', '_').replace('::', '_');

/**
 * Computes the order of items in the conversation sidebar, using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useConversationSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { conversation_sidebar_items_order: itemsOrder } = uiSettings.value;
    // BonBoZ: loc o BUOC CUOI, sau ca nhanh mac dinh lan nhanh merge - vi muc
    // can an co the dang nam trong thu tu nguoi dung da luu tu truoc.
    const boMucAn = list =>
      list.filter(i => !HIDDEN_CONVERSATION_SIDEBAR_ITEMS.includes(i.name));

    // If the sidebar order is not set, use the default order.
    if (!itemsOrder) {
      return boMucAn([...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER]);
    }
    // Create a copy of itemsOrder to avoid mutating the original store object.
    const itemsOrderCopy = [...itemsOrder];
    // If the sidebar order doesn't have the new elements, then add them to the list.
    DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER.forEach(item => {
      if (!itemsOrderCopy.find(i => i.name === item.name)) {
        itemsOrderCopy.push(item);
      }
    });
    return boMucAn(itemsOrderCopy);
  });
};

/**
 * Computes the order of items in the contact sidebar,using defaults if not present.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {Array} Ordered list of sidebar items.
 */
const useContactSidebarItemsOrder = uiSettings => {
  return computed(() => {
    const { contact_sidebar_items_order: itemsOrder } = uiSettings.value;
    return itemsOrder || DEFAULT_CONTACT_SIDEBAR_ITEMS_ORDER;
  });
};

/**
 * Toggles the open state of a sidebar item.
 * @param {string} key - The key of the sidebar item to toggle.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const toggleSidebarUIState = (key, uiSettings, updateUISettings) => {
  updateUISettings({ [key]: !uiSettings.value[key] });
};

/**
 * Sets the signature flag for a specific channel type in the inbox settings.
 * @param {string} channelType - The type of the channel.
 * @param {boolean} value - The value to set for the signature enabled flag.
 * @param {Function} updateUISettings - Function to update UI settings.
 */
const setSignatureFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_signature_enabled`]: value });
};

const setQuotedReplyFlagForInbox = (channelType, value, updateUISettings) => {
  if (!channelType) return;

  const slugifiedChannel = slugifyChannel(channelType);
  updateUISettings({ [`${slugifiedChannel}_quoted_reply_enabled`]: value });
};

/**
 * Fetches the signature flag for a specific channel type from UI settings.
 * @param {string} channelType - The type of the channel.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} The value of the signature enabled flag.
 */
const fetchSignatureFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_signature_enabled`];
};

const fetchQuotedReplyFlagFromUISettings = (channelType, uiSettings) => {
  if (!channelType) return false;

  const slugifiedChannel = slugifyChannel(channelType);
  return uiSettings.value[`${slugifiedChannel}_quoted_reply_enabled`];
};

/**
 * Checks if a specific editor hotkey is enabled.
 * @param {string} key - The key to check.
 * @param {Object} uiSettings - Reactive UI settings object.
 * @returns {boolean} True if the hotkey is enabled, otherwise false.
 */
const isEditorHotKeyEnabled = (key, uiSettings) => {
  const {
    editor_message_key: editorMessageKey,
    enter_to_send_enabled: enterToSendEnabled,
  } = uiSettings.value || {};
  if (!editorMessageKey) {
    // BonBoZ: mac dinh la ENTER de gui. Upstream mac dinh Ctrl+Enter, ma truc
    // page go xong theo phan xa la bam Enter - tin khong di, phai nhin xuong
    // nut moi biet. Ai da tu tat thi van ton trong lua chon do.
    const batEnter =
      enterToSendEnabled === undefined ? true : enterToSendEnabled;
    return key === (batEnter ? 'enter' : 'cmd_enter');
  }
  return editorMessageKey === key;
};

/**
 * Main composable function for managing UI settings.
 * @returns {Object} An object containing reactive properties and methods for UI settings management.
 */
export function useUISettings() {
  const getters = useStoreGetters();
  const store = useStore();
  const uiSettings = computed(() => getters.getUISettings.value);

  const updateUISettings = (settings = {}) => {
    store.dispatch('updateUISettings', {
      uiSettings: {
        ...uiSettings.value,
        ...settings,
      },
    });
  };

  return {
    uiSettings,
    updateUISettings,
    conversationSidebarItemsOrder: useConversationSidebarItemsOrder(uiSettings),
    contactSidebarItemsOrder: useContactSidebarItemsOrder(uiSettings),
    isContactSidebarItemOpen: key => !!uiSettings.value[key],
    toggleSidebarUIState: key =>
      toggleSidebarUIState(key, uiSettings, updateUISettings),
    setSignatureFlagForInbox: (channelType, value) =>
      setSignatureFlagForInbox(channelType, value, updateUISettings),
    fetchSignatureFlagFromUISettings: channelType =>
      fetchSignatureFlagFromUISettings(channelType, uiSettings),
    setQuotedReplyFlagForInbox: (channelType, value) =>
      setQuotedReplyFlagForInbox(channelType, value, updateUISettings),
    fetchQuotedReplyFlagFromUISettings: channelType =>
      fetchQuotedReplyFlagFromUISettings(channelType, uiSettings),
    isEditorHotKeyEnabled: key => isEditorHotKeyEnabled(key, uiSettings),
  };
}
